import { useCallback, useRef, useState } from 'react';

// A generic undo/redo history for a single editable document (here: the edit
// plan's timeline). It is the single writer of that value: callers push new states
// with `set`, and `undo`/`redo` restore previous ones — each of which also calls
// `apply` so the external React state (and its autosave) stays in lock-step.
//
// History is persisted to localStorage per document id, so it survives a page
// reload within the session. Snapshots are whole documents (small JSON), capped so
// the stacks can't grow without bound.

const MAX_ENTRIES = 50;

interface Stacks<T> { past: T[]; future: T[]; }

function storageKey(prefix: string, id: string) { return `${prefix}:${id}`; }

function loadStacks<T>(key: string): Stacks<T> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { past: [], future: [] };
    const v = JSON.parse(raw);
    return { past: Array.isArray(v.past) ? v.past : [], future: Array.isArray(v.future) ? v.future : [] };
  } catch { return { past: [], future: [] }; }
}

export interface EditHistory<T> {
  canUndo: boolean;
  canRedo: boolean;
  // Record a new state (applies it and clears the redo stack).
  set: (next: T) => void;
  undo: () => void;
  redo: () => void;
  // Point the history at a document: set the current value and load that
  // document's persisted stacks. Pass id=null to detach (no persistence).
  reset: (current: T, id: string | null) => void;
  // Forget a document's persisted history (e.g. when it's deleted).
  clear: (id: string) => void;
  // The latest value (for computing the next state from the freshest base).
  current: () => T;
}

export function useEditHistory<T>(keyPrefix: string, apply: (value: T) => void, emptyValue: T): EditHistory<T> {
  const currentRef = useRef<T>(emptyValue);
  const pastRef = useRef<T[]>([]);
  const futureRef = useRef<T[]>([]);
  const idRef = useRef<string | null>(null);
  const [, bump] = useState(0);
  const refresh = useCallback(() => bump((n) => n + 1), []);

  // Keep the latest apply callback without making the memoized methods depend on it.
  const applyRef = useRef(apply);
  applyRef.current = apply;

  const persist = useCallback(() => {
    if (!idRef.current) return;
    try {
      localStorage.setItem(storageKey(keyPrefix, idRef.current), JSON.stringify({ past: pastRef.current, future: futureRef.current }));
    } catch { /* quota / disabled — history still works in-memory this session */ }
  }, [keyPrefix]);

  const set = useCallback((next: T) => {
    pastRef.current = [...pastRef.current, currentRef.current].slice(-MAX_ENTRIES);
    futureRef.current = [];
    currentRef.current = next;
    applyRef.current(next);
    persist();
    refresh();
  }, [persist, refresh]);

  const undo = useCallback(() => {
    if (pastRef.current.length === 0) return;
    const prev = pastRef.current[pastRef.current.length - 1];
    pastRef.current = pastRef.current.slice(0, -1);
    futureRef.current = [currentRef.current, ...futureRef.current].slice(0, MAX_ENTRIES);
    currentRef.current = prev;
    applyRef.current(prev);
    persist();
    refresh();
  }, [persist, refresh]);

  const redo = useCallback(() => {
    if (futureRef.current.length === 0) return;
    const nxt = futureRef.current[0];
    futureRef.current = futureRef.current.slice(1);
    pastRef.current = [...pastRef.current, currentRef.current].slice(-MAX_ENTRIES);
    currentRef.current = nxt;
    applyRef.current(nxt);
    persist();
    refresh();
  }, [persist, refresh]);

  const reset = useCallback((current: T, id: string | null) => {
    idRef.current = id;
    currentRef.current = current;
    const stacks = id ? loadStacks<T>(storageKey(keyPrefix, id)) : { past: [], future: [] };
    pastRef.current = stacks.past;
    futureRef.current = stacks.future;
    refresh();
  }, [keyPrefix, refresh]);

  const clear = useCallback((id: string) => {
    try { localStorage.removeItem(storageKey(keyPrefix, id)); } catch { /* ignore */ }
    if (idRef.current === id) { pastRef.current = []; futureRef.current = []; refresh(); }
  }, [keyPrefix, refresh]);

  const current = useCallback(() => currentRef.current, []);

  return {
    canUndo: pastRef.current.length > 0,
    canRedo: futureRef.current.length > 0,
    set, undo, redo, reset, clear, current,
  };
}
